import {UserDefaultService} from 'app/blocks/service/api/user-default.service';
import {AfterViewInit, Directive, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DEFAULT_PAGE_SIZE_OPTIONS} from 'app/app.constants';
import {getTimeText} from 'app/common/utils/date-utils';
import {Observable, BehaviorSubject, of, combineLatest, Subject} from 'rxjs';
import {IBaseEntity} from 'app/blocks/model/base-entity.model';
import {AbstractEntityResource} from 'app/blocks/resource/abstract-entity-resource';
import {CurrencyService} from 'app/blocks/service/api/currency.service';
import {DiDate} from 'app/blocks/util/di-date';
import {FilterParams} from 'app/blocks/util/filter-params';
import {FilterSpec} from 'app/blocks/util/filter-spec';
import {ListViewParams} from 'app/blocks/util/list-view-params';
import {EntityListHeaderParams} from 'app/blocks/entity/entity-list-header-params';
import {FilterParamMap} from 'app/blocks/entity/entity-list.component';
import {debounceTime, takeUntil, tap} from 'rxjs/operators';
import {DiListPageHeaderComponent} from 'app/common/di-list-page-header/di-list-page-header.component';
import {TitleService} from 'app/blocks/service/title.service';
import {ExportService} from 'app/blocks/service/api/export.service';

type NzSortDirection = 'ascend' | 'descend' | null;

@Directive({
    standalone: false
})
// tslint:disable-next-line:directive-class-suffix
export abstract class EntityCustomListComponent<T extends IBaseEntity> implements OnInit, OnDestroy, AfterViewInit {
    headerParams: any;

    readonly viewParams$: Observable<ListViewParams>;

    readonly entityListBehaviourSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    entityList$: Observable<any[]> = this.entityListBehaviourSubject$.asObservable();
    readonly defaultPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
    readonly showSizeChanger = true;
    readonly tablePaginationSize = 'small';
    readonly showQuickJumper = true;
    readonly borderedTable = false;
    readonly activeFilterCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    readonly activeFilterCountParams$: Observable<number> = this.activeFilterCountSubject.asObservable();
    readonly filterParamsSubject: BehaviorSubject<FilterParams[]> = new BehaviorSubject<FilterParams[]>([]);
    readonly filterParams$: Observable<FilterParams[]> = this.filterParamsSubject.asObservable();
    someIdsSelected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    _unsubscribeAll: Subject<void>;

    totalItems = 0;
    totalItems$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    pageIndex = 1;
    pageSize = 25;
    selectedRequest = null;

    filterSpec: FilterSpec;
    filterParamMap: FilterParamMap = {};
    ngFilterModel: {[key: string]: any} = {};

    selectedIds = [];
    selectedIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
    lastEntitySelected = null;
    lastEntitySelected$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
    readonly masterCheckboxStatus$: Observable<any>;

    @ViewChild(DiListPageHeaderComponent, {static: false})
    nzHeader: DiListPageHeaderComponent;

    public _groupActionSubject = new BehaviorSubject(false);
    groupAction$ = this._groupActionSubject.asObservable();

    rowActions: any[] = [];
    exportCount = 1;
    exportType = '';
    loading = false;

    EXPORT_ALL = {
        label: 'exportAll',
        dynamicValue: this.totalItems$.getValue(),
        icon: 'file_copy',
        action: this.exportAll.bind(this),
        enabled$: of(true)
    };

    EXPORT_SELECTED = {
        label: 'exportSelected',
        dynamicValue: 0,
        icon: 'file_copy',
        action: this.exportSelected.bind(this),
        enabled$: of(true)
    };

    sortName = 'id';
    sortDir: NzSortDirection = 'ascend';
    sortParams: any = {};
    searchEnabled = true;

    constructor(
        public readonly resource: AbstractEntityResource<T>,
        public currencyService: CurrencyService,
        public _translateService: TranslateService,
        public _titleService?: TitleService,
        public userDefaultService?: UserDefaultService,
        private _exportService?: ExportService
    ) {
        this._unsubscribeAll = new Subject<void>();
        this.masterCheckboxStatus$ = this.getMasterCheckboxStatus();
        this.selectedIds$.subscribe((ids) => {
            this.someIdsSelected$.next(ids.length > 0);
        });
        this.exportType = this.getExportType();
    }

    // override this for custom naming
    getPageName() {
        return this.resource.name.toString();
    }

    getEntityGroupActions(): any {
        const groupActions = [];
        if (this.isExportAll()) {
            groupActions.push(this.EXPORT_ALL);
        }
        if (this.isExportAll()) {
            groupActions.push(this.EXPORT_SELECTED);
        }
        return groupActions;
    }

    formatDateString(date: string | DiDate): string {
        return DiDate.inUKDateFormat(date as string);
    }

    getDateTimeAsString(dateStr: string): string {
        return getTimeText(dateStr);
    }

    pageIndexChange($event: any): void {
        this.loadRequests();
    }

    pageSizeChange($event: any): void {
        this.loadRequests();
    }

    sortBy(sortName: string, value: any | NzSortDirection): void {
        try {
            this.sortName = value === null ? this.getDefaultSort() : sortName;
            this.sortDir = value === null ? 'ascend' : value;
            for (const key of Object.keys(this.sortParams)) {
                this.sortParams[key] = key === sortName ? value : null;
            }
            this.sortParams[sortName] = value;
            this.loadRequests();
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    getSortParam() {
        return this.sortName + ',' + (this.sortParams[this.sortName] == 'ascend' ? 'asc' : 'desc');
    }

    ngOnInit(): void {
        if (this._titleService) {
            this._translateService.get(`${this.resource.name}.pages.list.heading`).subscribe((heading) => {
                this._titleService.setTitle(heading);
            });
        }
        this.rowActions = this.getEntityActions();
        this.headerParams = {
            icon: this.resource.icon,
            resource: this.resource,
            showAddButton: false,
            groupActions: this.getEntityGroupActions(),
            groupActionProcessing: this.groupAction$
        } as EntityListHeaderParams;
        this.filterSpec = this.getFilterSpec();
        this.initializeFilterData();
        this.getPageFilters().then((val) => {
            this.setupParamListener();
        });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        if (this.nzHeader && this.nzHeader.search && this.searchEnabled) {
            this.nzHeader.search.searchTerm$.subscribe((searchTerm) => {
                this.loadRequests(searchTerm);
            });
        }
    }

    setupParamListener() {
        this.filterParamsSubject.pipe(debounceTime(250)).subscribe((update) => {
            this.loadRequests();
        });
    }

    getPageFilters(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.getServerFilters().then((val) => {
                resolve(true);
            });
        });
    }

    getServerFilters(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            if (this.userDefaultService) {
                this.userDefaultService.getUserDefaultsForScreen(this.getPageName()).then((filters) => {
                    for (const filter of filters.body) {
                        this.updateFilter(filter.filter, filter.value);
                    }
                    resolve(true);
                });
            }
            resolve(true);
        });
    }

    // override this with specific request
    loadRequests(searchterm?: string) {
        this.entityList$ = of([]);
        this.totalItems = 0;
    }

    getFilterSpec(): FilterSpec {
        return {};
    }

    initializeFilterData(): void {
        if (this.filterSpec) {
            for (const filterColumn of Object.keys(this.filterSpec)) {
                const spec = this.filterSpec[filterColumn];
                let initialValue = null;
                switch (spec.filterType) {
                    case 'checkbox':
                        initialValue = [];
                        if (spec.objectPath) {
                            const object = this._translateService.instant(spec.objectPath);
                            // translation is not available at the moment
                            if (object !== spec.objectPath) {
                                for (const key of Object.keys(object)) {
                                    initialValue.push({
                                        text: object[key],
                                        value: key,
                                        byDefault: false
                                    });
                                }
                            }
                        }
                        break;
                    case 'date-range':
                        initialValue = [];
                        break;
                    case 'search':
                        initialValue = null;
                        break;
                }
                this.ngFilterModel[filterColumn] = initialValue;
            }
        }
    }

    clearFilters(): void {
        this.initializeFilterData();
        this.filterParamMap = {};
        this.activeFilterCountSubject.next(0);
        this.filterParamsSubject.next(Object.values(this.filterParamMap));
        this.loadRequests();
    }

    getFilterMap(): any {
        const appliedFilters = {};
        const filters = Object.keys(this.filterParamMap);
        filters.forEach((filter) => {
            const filterData = this.filterParamMap[filter];
            if (filterData) {
                appliedFilters[filter] = filterData;
            }
        });
        appliedFilters['page'] = this.pageIndex - 1;
        appliedFilters['size'] = this.pageSize;
        appliedFilters['sort'] = this.getSortParam();
        this.setNonChangeFilters(appliedFilters);
        return appliedFilters;
    }

    // override to define hard coded filters, for example invoiceable only
    setNonChangeFilters(appliedFilters: any) {}

    updateFilter(columnName: string, value: any): void {
        if (columnName && this.filterSpec[columnName]) {
            const filterSpec = this.filterSpec[columnName];
            const filterType = filterSpec.filterType;
            let currentValue: any;
            let extra = null;
            switch (filterType) {
                case 'date-range':
                    if (value && value[0] && value[1]) {
                        currentValue = [DiDate.newInstance(value[0]), DiDate.newInstance(value[1])];
                    }
                    break;
                case 'single-date':
                    if (value) {
                        currentValue = DiDate.newInstance(value);
                    } else {
                        currentValue = null;
                    }
                    break;
                case 'checkbox':
                    if (!value || value.length === 0) {
                        currentValue = null;
                    } else {
                        currentValue = value;
                    }
                    break;
                case 'search':
                    if (value && value.id) {
                        currentValue = value.id;
                        if (!filterSpec.searchDisplayAttribute) {
                            throw new Error('FilterSpec for search should specify a display attribute!');
                        }
                        const displayAttribute = value[filterSpec.searchDisplayAttribute];
                        extra = {
                            [filterSpec.searchDisplayAttribute]: displayAttribute
                        };
                    }
                    break;
                default:
                    throw new Error(`Unrecognized filter type ${filterType}`);
            }
            if (currentValue) {
                if (extra) {
                    this.filterParamMap[columnName] = currentValue;
                } else {
                    this.filterParamMap[columnName] = currentValue;
                }
            } else {
                if (this.filterParamMap[columnName]) {
                    delete this.filterParamMap[columnName];
                }
            }
            this.filterParamsSubject.next(Object.values(this.filterParamMap));
            this.activeFilterCountSubject.next(Object.keys(this.filterParamMap).length);
        } else {
            throw new Error(`${columnName} is not part of the FilterSpec! Will be ignored!`);
        }
    }

    onMasterCheckboxChanged(checked: boolean): void {
        if (checked) {
            this.entityList$.pipe().subscribe((entityList) => {
                this.selectedIds = entityList.map((entity) => entity.id);
            });
        } else {
            this.selectedIds = [];
        }
        this.selectedIds$.next(this.selectedIds);
        this.someIdsSelected$.next(this.selectedIds.length > 0);
    }

    toggleEntitySelection = (entity: T, event: any): void => {
        if (event.checked) {
            this.selectedIds.push(entity.id);
            this.lastEntitySelected = entity;
        } else {
            const index = this.selectedIds.findIndex((id) => id === entity.id);
            this.selectedIds.splice(index, 1);
            this.lastEntitySelected = null;
        }
        this.selectedIds$.next(this.selectedIds);
        this.someIdsSelected$.next(this.selectedIds.length > 0);
        this.lastEntitySelected$.next(this.lastEntitySelected);
    };

    selectedIdsIncludesEntityId(id: any): boolean {
        return this.selectedIds.includes(parseInt(id));
    }

    getMasterCheckboxStatus(): Observable<any> {
        return combineLatest(this.entityList$, this.selectedIds$, (latestEntityList, latestSelectedIds) => {
            let checked = false;
            let indeterminate = false;
            if (latestEntityList.length > 0) {
                const currentPageSelectedIds = latestEntityList.map((entity) => entity.id).filter((id) => latestSelectedIds.includes(id));
                if (currentPageSelectedIds.length > 0) {
                    checked = currentPageSelectedIds.length === latestEntityList.length;
                    indeterminate = currentPageSelectedIds.length < latestEntityList.length;
                }
            }
            return {checked, indeterminate};
        });
    }

    getEntityActions(): any[] {
        const groupActions = [];
        return groupActions;
    }

    getExportType(): string {
        return '';
    }

    isExportAll(): boolean {
        return false;
    }

    exportAll(): void {
        const count = this.totalItems;
        const exportType = this.exportType;
        const obj = this.getFilterMap();
        obj['count'] = count;
        this._groupActionSubject.next(true);
        this._exportService.exportAll(obj, exportType).then(() => {
            this._groupActionSubject.next(false);
        });
    }

    exportSelected(): void {
        this.selectedIds$.pipe().subscribe((data) => {
            this.selectedIds = data;
        });
        this._groupActionSubject.next(true);
        this._exportService.exportSelected(this.selectedIds, this.exportType).then(() => {
            this._groupActionSubject.next(false);
        });
    }

    getDefaultSort(): string {
        return 'id';
    }
}
